import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { setAuthToken, login } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { MouseEffectBackground } from "@/components/mouse-effect-background";

export function LoginPage() {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(password);
      setAuthToken(password);
      navigate({ to: "/" });
    } catch (err) {
      if (err instanceof Error && err.message === 'Unauthorized') {
        setError("Invalid password");
      } else {
        setError("Connection error. Is the API running?");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 relative">
      <MouseEffectBackground />
      <Card className="w-full max-w-md relative z-10">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="-100 -40 200 80" style={{ height: '48px' }}>
              <defs>
                <style>{`
                  .pill-bg {
                    fill: #000000;
                  }
                  .pill-border {
                    fill: none;
                    stroke: #000000;
                    stroke-width: 3;
                  }
                  .pill-text {
                    fill: #ffffff;
                    font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
                    font-weight: 700;
                    font-size: 32px;
                    letter-spacing: 4;
                  }
                  .pill-pulse {
                    fill: none;
                    stroke: #00ff7a;
                    stroke-width: 4;
                    stroke-linecap: round;
                    stroke-dasharray: 80 1000;
                    stroke-dashoffset: 0;
                    animation: pillPulse 2.2s ease-in-out infinite;
                  }
                  @keyframes pillPulse {
                    from { stroke-dashoffset: 0; }
                    to   { stroke-dashoffset: -1080; }
                  }
                `}</style>
              </defs>

              {/* black pill */}
              <rect className="pill-bg"
                    x="-84" y="-24"
                    width="168" height="48" rx="24" />

              <rect className="pill-border"
                    x="-84" y="-24"
                    width="168" height="48" rx="24" />

              {/* pulse path exactly following pill outline, start at bottom centre */}
              <path className="pill-pulse"
                    d="
                      M 0 24
                      H 60
                      A 24 24 0 0 0 84 0
                      A 24 24 0 0 0 60 -24
                      H -60
                      A 24 24 0 0 0 -84 0
                      A 24 24 0 0 0 -60 24
                      H 0
                    " />

              {/* OREO text */}
              <text className="pill-text"
                    x="0" y="10"
                    textAnchor="middle">
                OREO
              </text>
            </svg>
          </div>
          <CardDescription>Operational Response & Engagement Operator</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
              />
            </div>
            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign In
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
